import React, { useEffect, useState } from "react";
import { useAppContext } from "../../context/AppContext";
import { useNavigate } from "react-router-dom";
import { ReferralsAPI } from "../../api/referral";
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  TextField,
} from "@mui/material";
import { NumericFormat } from "react-number-format";
import dayjs from "dayjs";
import { GridCloseIcon } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers";
import { ClosedBusinessesAPI } from "../../api/closed-business";
import {
  memberOptionGroup,
  memberOptionLabel,
  sortByName,
  sortMembersDepartedLast,
} from "../helper";
import renderMemberOption from "../MemberOption";
import { MembershipAPI } from "../../api/membership";
import minMax from "dayjs/plugin/minMax"; // Import the plugin

dayjs.extend(minMax);

const AddClosedBusinessModal = ({ open, handleClose, setIsLoading }) => {
  const { openSnack } = useAppContext();
  const navigate = useNavigate();
  const [referrals, setReferrals] = useState([]);

  const [formState, setFormState] = useState({
    closed_business_date: "",
    dollar_amount_of_realized_revenue: "",
    referral_id: "",

    invitation_type: "independent",

    closed_business_name: "",
    closed_business_sender_id: "",
    closed_business_receiver_id: "",

    // Recurrent payment fields
    recurrent: false,
    period: "",
    first_day_of_payment: "",
    last_day_of_payment: "",
    dollar_amount_per_period: "",
  });

  const [formError, setFormError] = useState({});
  const [members, setMembers] = useState([]);

  const fetchMembers = async () => {
    try {
      const response = await MembershipAPI.getMembers("");
      setMembers(response?.items || []);
    } catch (e) {
      const status = e.response?.status;
      if (status === 401) {
        navigate("/login");
      }
      console.error("Error during fetching report", e);
    }
  };

  useEffect(() => {
    fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleError = (formObj = formState) => {
    const temp = { ...formError };

    if ("closed_business_date" in formObj) {
      temp.closed_business_date = formObj.closed_business_date
        ? ""
        : "Closed Business Date is required.";
    }

    if ("dollar_amount_of_realized_revenue" in formObj) {
      temp.dollar_amount_of_realized_revenue =
        formObj.dollar_amount_of_realized_revenue
          ? ""
          : "Dollar Amount of Realized Revenue is required.";
    }

    if (formState?.invitation_type === "referral_based") {
      if ("referral_id" in formObj) {
        temp.referral_id = formObj.referral_id
          ? ""
          : "Referral sender is required.";
      }
    } else {
      if ("closed_business_name" in formObj) {
        temp.closed_business_name = formObj.closed_business_name
          ? ""
          : "Closed Business Name is required.";
      }
      if ("closed_business_sender_id" in formObj) {
        temp.closed_business_sender_id = formObj.closed_business_sender_id
          ? ""
          : "Closed Business Sender is required.";
      }
    }

    // Validate recurrent payment fields

    if (formState?.recurrent) {
      if ("first_day_of_payment" in formObj) {
        temp.first_day_of_payment = formObj.first_day_of_payment
          ? ""
          : "First day of payment is required when recurrent is enabled.";
      }
      if ("period" in formObj) {
        temp.period = formObj.period
          ? ""
          : "Period is required when recurrent is enabled.";
      }
      if ("dollar_amount_per_period" in formObj) {
        temp.dollar_amount_per_period = formObj.dollar_amount_per_period
          ? ""
          : "Dollar amount per period is required when recurrent is enabled.";
      }
    }

    setFormError({
      ...temp,
    });

    return Object.values(temp).every((x) => x === "");
  };

  const handleChange = (key, value) => {
    setFormState((prevState) => ({ ...prevState, [key]: value }));
    handleError({ [key]: value });
  };

  const fetchReferrals = async () => {
    try {
      const response = await ReferralsAPI.getReferrals();
      setReferrals(response?.items || []);
    } catch (e) {
      const status = e.response?.status;
      if (status === 401) {
        navigate("/login");
      }
      console.error("Error during fetching report", e);
    }
  };

  useEffect(() => {
    fetchReferrals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getMinValidDate = (date1, date2) => {
    if (!date1 || !date2) return null;
    const d1 = dayjs(date1);
    const d2 = dayjs(date2);

    return dayjs.min(d1, d2);
  };

  const renderForm = () => {
    return (
      <Grid
        container
        spacing={2}
        rowSpacing={2.5}
        sx={{
          padding: "16px 0",
        }}
      >
        {formState?.invitation_type === "referral_based" ? (
          <>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                value={formState?.referral_id || null}
                onChange={(_e, newValue) =>
                  handleChange("referral_id", newValue)
                }
                id="multiple-limit-tags"
                options={sortByName(referrals)}
                getOptionLabel={(option) => `${option?.referral_name}`}
                renderOption={(props, option, { selected }) => (
                  <li
                    {...props}
                    style={{
                      backgroundColor: selected && "#EAF2FF",
                      fontSize: "14px",
                      lineHeight: "20px",
                    }}
                  >
                    {`${option?.referral_name}`}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Referral Name*"
                    className="login-textField"
                    size="small"
                    error={!!formError.referral_id}
                    helperText={formError.referral_id}
                  />
                )}
                className="login-textField"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="normal"
                fullWidth
                id="referral_sender"
                label="Referral Sender*"
                name="referral_sender"
                value={`${
                  formState?.referral_id?.referral_sender?.first_name || ""
                }${
                  formState?.referral_id?.referral_sender?.last_name
                    ? ` ${formState?.referral_id?.referral_sender?.last_name}`
                    : ""
                }`}
                onChange={(e) =>
                  handleChange("referral_sender", e.target.value)
                }
                className="login-textField "
                size="small"
                readOnly
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                margin="normal"
                fullWidth
                id="referral_receiver"
                label="Referral Receiver*"
                name="referral_receiver"
                value={`${
                  formState?.referral_id?.referral_receiver?.first_name || ""
                }${
                  formState?.referral_id?.referral_receiver?.last_name
                    ? ` ${formState?.referral_id?.referral_receiver?.last_name}`
                    : ""
                }`}
                onChange={(e) =>
                  handleChange("referral_receiver", e.target.value)
                }
                className="login-textField "
                size="small"
                readOnly
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Referral Date*"
                value={
                  formState?.referral_id?.referral_date
                    ? dayjs(formState?.referral_id?.referral_date)
                    : null
                }
                onChange={(newValue) => handleChange("referral_date", newValue)}
                className="login-textField date-textField"
                sx={{ width: "100%", height: "40px" }}
                format="MM/DD/YYYY"
                slotProps={{
                  textField: {
                    margin: "normal",
                    fullWidth: true,
                    id: "referral_date",
                    name: "referral_date",
                    className: "login-textField",
                    size: "small",
                  },
                }}
                readOnly
              />
            </Grid>
          </>
        ) : (
          <>
            <Grid item xs={12} sm={6}>
              {" "}
              <TextField
                margin="normal"
                fullWidth
                id="closed_business_name"
                label="Closed Business Name*"
                name="closed_business_name"
                value={formState?.closed_business_name}
                onChange={(e) =>
                  handleChange("closed_business_name", e.target.value)
                }
                className="login-textField "
                size="small"
                error={!!formError.closed_business_name}
                helperText={formError.closed_business_name}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                value={formState?.closed_business_sender_id || null}
                onChange={(_e, newValue) =>
                  handleChange("closed_business_sender_id", newValue)
                }
                id="multiple-limit-tags"
                options={sortMembersDepartedLast(members)}
                groupBy={memberOptionGroup}
                getOptionLabel={memberOptionLabel}
                isOptionEqualToValue={(option, value) =>
                  option?.id === value?.id
                }
                renderOption={renderMemberOption}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Closed Business Sender*"
                    className="login-textField"
                    size="small"
                    error={!!formError.closed_business_sender_id}
                    helperText={formError.closed_business_sender_id}
                  />
                )}
                className="login-textField"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                value={formState?.closed_business_receiver_id || null}
                onChange={(_e, newValue) =>
                  handleChange("closed_business_receiver_id", newValue)
                }
                id="multiple-limit-tags"
                options={sortMembersDepartedLast(members)}
                groupBy={memberOptionGroup}
                getOptionLabel={memberOptionLabel}
                isOptionEqualToValue={(option, value) =>
                  option?.id === value?.id
                }
                renderOption={renderMemberOption}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Closed Business Receiver*"
                    className="login-textField"
                    size="small"
                  />
                )}
                className="login-textField"
                size="small"
              />
            </Grid>
          </>
        )}
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DatePicker
            label="Closed Business Date*"
            value={formState?.closed_business_date || null}
            onChange={(newValue) =>
              handleChange("closed_business_date", newValue)
            }
            className="login-textField date-textField"
            sx={{ width: "100%", height: "40px" }}
            format="MM/DD/YYYY"
            slotProps={{
              textField: {
                margin: "normal",
                fullWidth: true,
                id: "closed_business_date",
                name: "closed_business_date",
                className: "login-textField",
                size: "small",
                error: !!formError.closed_business_date,
                helperText: formError.closed_business_date,
              },
            }}
            minDate={
              formState?.invitation_type === "referral_based"
                ? dayjs(formState?.referral_id?.referral_date)
                : getMinValidDate(
                    formState?.closed_business_receiver_id?.date_started,
                    formState?.closed_business_sender_id?.date_started,
                  )
            }
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <NumericFormat
            value={formState?.dollar_amount_of_realized_revenue}
            onValueChange={(e) =>
              handleChange("dollar_amount_of_realized_revenue", e.value)
            }
            customInput={TextField}
            thousandSeparator
            valueIsNumericString
            fixedDecimalScale
            decimalScale={2}
            prefix="$"
            label="Dollar Amount of Realized Revenue*"
            className="login-textField"
            error={!!formError.dollar_amount_of_realized_revenue}
            helperText={formError.dollar_amount_of_realized_revenue}
            size="small"
          />
        </Grid>

        {/* Recurrent Payment Section */}
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={12}>
          <h3
            style={{
              margin: "0 0 16px 0",
              fontSize: "16px",
              fontWeight: "500",
            }}
          >
            Recurrent Payment Settings
          </h3>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            select
            margin="normal"
            fullWidth
            id="recurrent"
            label="Recurrent Payment"
            name="recurrent"
            value={formState?.recurrent}
            onChange={(e) => handleChange("recurrent", e.target.value)}
            className="login-textField"
            size="small"
          >
            <MenuItem value={false}>No</MenuItem>
            <MenuItem value={true}>Yes</MenuItem>
          </TextField>
        </Grid>
        {formState?.recurrent && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                margin="normal"
                fullWidth
                id="period"
                label="Payment Period*"
                name="period"
                value={formState?.period}
                onChange={(e) => handleChange("period", e.target.value)}
                className="login-textField"
                size="small"
                error={!!formError.period}
                helperText={formError.period}
              >
                <MenuItem value="month">Month</MenuItem>
                <MenuItem value="quarter">Quarter</MenuItem>
                <MenuItem value="year">Year</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="First Day of Payment*"
                value={formState?.first_day_of_payment || null}
                onChange={(newValue) =>
                  handleChange("first_day_of_payment", newValue)
                }
                className="login-textField date-textField"
                sx={{ width: "100%", height: "40px" }}
                format="MM/DD/YYYY"
                slotProps={{
                  textField: {
                    margin: "normal",
                    fullWidth: true,
                    id: "first_day_of_payment",
                    name: "first_day_of_payment",
                    className: "login-textField",
                    size: "small",
                    error: !!formError.first_day_of_payment,
                    helperText: formError.first_day_of_payment,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Last Day of Payment (Optional)"
                value={formState?.last_day_of_payment || null}
                onChange={(newValue) =>
                  handleChange("last_day_of_payment", newValue)
                }
                className="login-textField date-textField"
                sx={{ width: "100%", height: "40px" }}
                format="MM/DD/YYYY"
                slotProps={{
                  textField: {
                    margin: "normal",
                    fullWidth: true,
                    id: "last_day_of_payment",
                    name: "last_day_of_payment",
                    className: "login-textField",
                    size: "small",
                  },
                }}
                minDate={
                  formState?.first_day_of_payment
                    ? dayjs(formState?.first_day_of_payment)
                    : null
                }
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              sx={{ marginTop: formError?.first_day_of_payment ? "16px" : "0" }}
            >
              <NumericFormat
                value={formState?.dollar_amount_per_period}
                onValueChange={(e) =>
                  handleChange("dollar_amount_per_period", e.value)
                }
                customInput={TextField}
                thousandSeparator
                valueIsNumericString
                fixedDecimalScale
                decimalScale={2}
                prefix="$"
                label="Dollar Amount per Period*"
                className="login-textField"
                error={!!formError.dollar_amount_per_period}
                helperText={formError.dollar_amount_per_period}
                size="small"
              />
            </Grid>
          </>
        )}
      </Grid>
    );
  };

  const AddClosedBusiness = async (form) => {
    try {
      const {
        closed_business_name,
        closed_business_date,
        dollar_amount_of_realized_revenue,
        closed_business_sender_id,
        closed_business_receiver_id,
        referral_id,
        invitation_type,
      } = form || {};

      const payload =
        invitation_type === "referral_based"
          ? {
              closed_business_date:
                dayjs(closed_business_date).format("YYYY-MM-DD"),
              dollar_amount_of_realized_revenue,
              referral_id: referral_id?.id,
              // Recurrent payment fields
              recurrent: form?.recurrent || false,
              period: form?.recurrent ? form?.period : null,
              first_day_of_payment:
                form?.recurrent && form?.first_day_of_payment
                  ? dayjs(form?.first_day_of_payment).format("YYYY-MM-DD")
                  : null,
              last_day_of_payment:
                form?.recurrent && form?.last_day_of_payment
                  ? dayjs(form?.last_day_of_payment).format("YYYY-MM-DD")
                  : null,
              dollar_amount_per_period: form?.recurrent
                ? form?.dollar_amount_per_period
                : null,
            }
          : {
              closed_business_name,
              closed_business_date:
                dayjs(closed_business_date).format("YYYY-MM-DD"),
              dollar_amount_of_realized_revenue,
              closed_business_sender_id: closed_business_sender_id?.id,
              closed_business_receiver_id: closed_business_receiver_id?.id,
              // Recurrent payment fields
              recurrent: form?.recurrent || false,
              period: form?.recurrent ? form?.period : null,
              first_day_of_payment:
                form?.recurrent && form?.first_day_of_payment
                  ? dayjs(form?.first_day_of_payment).format("YYYY-MM-DD")
                  : null,
              last_day_of_payment:
                form?.recurrent && form?.last_day_of_payment
                  ? dayjs(form?.last_day_of_payment).format("YYYY-MM-DD")
                  : null,
              dollar_amount_per_period: form?.recurrent
                ? form?.dollar_amount_per_period
                : null,
            };

      await ClosedBusinessesAPI.addClosedBusiness(
        payload,
        form?.invitation_type,
      );

      openSnack("Closed Business created successfully.", "success");
      setIsLoading(true);
      handleClose();
    } catch (error) {
      console.error("Error adding Closed Business:", error);
    }
  };

  const handleSubmit = () => {
    if (handleError()) {
      AddClosedBusiness(formState);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) =>
        reason === "backdropClick" ? "" : handleClose()
      }
      hideBackdrop
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className="custom-modal"
      sx={{
        ".MuiPaper-root": {
          width: "834px",
        },
      }}
    >
      <div className="modal-header">
        <h2 className="header-text">Create New Closed Business</h2>
        <IconButton onClick={() => handleClose()}>
          <GridCloseIcon />
        </IconButton>
      </div>
      {renderForm()}

      <DialogActions
        sx={{ paddingTop: `${formError?.dues_paid_date ? "24px" : "8px"}` }}
        className="modal-btn-container"
      >
        <Button onClick={handleClose} className="button-common light-blue-bg">
          Cancel
        </Button>
        <Button onClick={handleSubmit} className="add-new-button button-common">
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddClosedBusinessModal;
